import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Crown, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";

export default function PremiumSuccess() {
    const nav = useNavigate();
    const { refresh } = useAuth();
    const [params] = useSearchParams();
    const [state, setState] = useState({ status: "checking", message: "Verifying payment…" });
    const attempts = useRef(0);

    useEffect(() => {
        const sessionId = params.get("session_id");
        if (!sessionId) {
            setState({ status: "error", message: "Missing session id" });
            return;
        }

        const poll = async () => {
            try {
                const { data } = await api.get(`/billing/status/${sessionId}`);
                if (data.payment_status === "paid" || data.applied) {
                    await refresh();
                    setState({ status: "success", message: "You're premium!" });
                    return;
                }
                if (data.status === "expired") {
                    setState({ status: "error", message: "Payment session expired" });
                    return;
                }
                if (attempts.current++ >= 8) {
                    setState({ status: "error", message: "Still processing. Check back in a moment." });
                    return;
                }
                setTimeout(poll, 2000);
            } catch {
                if (attempts.current++ >= 8) {
                    setState({ status: "error", message: "Could not verify payment" });
                    return;
                }
                setTimeout(poll, 2000);
            }
        };
        poll();
    }, [params, refresh]);

    return (
        <div className="min-h-screen flex flex-col items-center justify-center px-6 text-white text-center">
            <Logo size="sm" className="mb-8" />
            {state.status === "checking" && (
                <div data-testid="premium-success-checking">
                    <Loader2 className="w-12 h-12 text-dash animate-spin mx-auto mb-4" />
                    <h1 className="font-display text-3xl mb-2">VERIFYING…</h1>
                    <p className="text-sm text-white/60">{state.message}</p>
                </div>
            )}
            {state.status === "success" && (
                <div data-testid="premium-success-done">
                    <CheckCircle2 className="w-16 h-16 text-dash mx-auto mb-4" />
                    <h1 className="font-display text-5xl text-dash mb-2">WELCOME TO<br />SMASH+.</h1>
                    <p className="text-white/70 text-sm mb-8 max-w-sm mx-auto">
                        Unlimited swipes and message anyone you swipe. The gloves are off.
                    </p>
                    <button
                        onClick={() => nav("/discover")}
                        data-testid="premium-success-go-btn"
                        className="h-14 px-10 bg-smash hover:bg-smash/90 text-white font-display text-lg tracking-wider uppercase"
                    >
                        Let's Go
                    </button>
                </div>
            )}
            {state.status === "error" && (
                <div data-testid="premium-success-error">
                    <XCircle className="w-12 h-12 text-smash mx-auto mb-4" />
                    <h1 className="font-display text-3xl mb-2">SOMETHING WENT WRONG.</h1>
                    <p className="text-sm text-white/60 mb-6">{state.message}</p>
                    <button
                        onClick={() => nav("/premium")}
                        className="h-12 px-6 border border-white/20 text-xs uppercase tracking-widest"
                    >
                        Back to Premium
                    </button>
                </div>
            )}
        </div>
    );
}
